// crewbit-admin/src/components/money/clients/MoneyDefaultsSection.tsx
//
// Tier 2-UI Phase D-admin §WorldClass.1 — client-edit Money Defaults
// section. Mounts as embedded card inside ClientDetailPanel slide-over.
// Operator sets per-client overrides for the 7 fields covered by Migration
// 275 (`clients.default_*` columns + the shared `notes_for_invoices`);
// every future invoice for this client pre-fills via the
// get_client_defaults RPC's 4-tier resolution.
//
// Body decisions mirror crewbit-mobile/components/money/clients/
// MoneyDefaultsSection.tsx per `feedback_admin_pair_must_match_mobile_body`:
// self-contained data layer (own SELECT + RPC fetch), 800ms debounce per
// field, optimistic local state, re-fetch defaults envelope after each
// save so OverridePill source flips client_default ↔ business_default as
// the operator sets/clears values.
//
// Reads via direct PostgREST (`supabase.from('clients').select(...)`) so
// the column-level "is this set or null?" semantic is visible. Writes via
// direct PostgREST update consistent with existing admin patterns — no
// SECDEF mutation RPC in Phase D substrate (§T2UI.PhaseD-mobile.D1;
// queued §WorldClass.1.UpdateClientDefaultsRpc for activity-log
// integration when audit-trail demand surfaces).
//
// Save discipline: 800ms debounce per field; SaveConfirmationPill flashes
// 'success' on save; flashes 'warning' on fail. Admin Toast is wired by
// the parent ClientDetailPanel — but failure surfacing here uses the
// inline pill, not Toast, since the section is one of many in the
// slide-over and a Toast would compete with section-level affordances.
// Tax-rate boundary conversion: UI percent → DB decimal at the supabase
// update boundary (Decision §D5; handled inside TaxTreatmentSelector).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useClientDefaults } from '../../../hooks/useClientDefaults';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import type {
  ClientDefaultsResolved,
  ClientDefaultsSources,
  ClientDefaultsReasoning,
  LateFeePolicy,
  TaxTreatment,
} from '../../../types/clientDefaults';
import OverridePill from './OverridePill';
import OverridePillTooltip from './OverridePillTooltip';
import ClientDefaultsRow from './ClientDefaultsRow';
import PolymorphicLateFeeEditor from './PolymorphicLateFeeEditor';
import TaxTreatmentSelector from './TaxTreatmentSelector';
import SaveConfirmationPill from '../settings/SaveConfirmationPill';

const SAVE_DEBOUNCE_MS = 800;
const PILL_VISIBLE_MS = 2000;

interface ClientRow {
  default_payment_terms_days: number | null;
  default_deposit_percentage: number | null;
  default_late_fee_policy: LateFeePolicy | null;
  default_late_fee_amount: number | null;
  default_tax_treatment: TaxTreatment | null;
  default_tax_rate_override: number | null; // decimal 0..1
  notes_for_invoices: string | null;
}

interface Props {
  clientId: string;
  clientName: string | null;
}

export default function MoneyDefaultsSection({ clientId, clientName }: Props) {
  const fetchDefaults = useClientDefaults();

  const [row, setRow] = useState<ClientRow | null>(null);
  const [resolved, setResolved] = useState<ClientDefaultsResolved | null>(null);
  const [sources, setSources] = useState<ClientDefaultsSources | null>(null);
  const [reasoning, setReasoning] = useState<ClientDefaultsReasoning | null>(null);
  const [loading, setLoading] = useState(true);
  const [pillVisible, setPillVisible] = useState(false);
  const [pillKind, setPillKind] = useState<'success' | 'warning'>('success');
  const [pillLabel, setPillLabel] = useState<string | undefined>(undefined);

  // Tooltip state keyed by field name so taps on different pills replace
  // the same overlay rather than stacking.
  const [tooltipFor, setTooltipFor] = useState<keyof ClientDefaultsSources | null>(null);

  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});
  const pillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refetchDefaults = useCallback(async () => {
    const result = await fetchDefaults({ clientId });
    if (result.success) {
      setResolved(result.data.resolved);
      setSources(result.data.sources);
      setReasoning(result.data.reasoning);
    }
  }, [clientId, fetchDefaults]);

  const refetchRow = useCallback(async () => {
    const { data, error } = await supabase
      .from('clients')
      .select(
        'default_payment_terms_days, default_deposit_percentage, default_late_fee_policy, default_late_fee_amount, default_tax_treatment, default_tax_rate_override, notes_for_invoices',
      )
      .eq('id', clientId)
      .maybeSingle();
    if (!error && data) setRow(data as ClientRow);
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      await Promise.all([refetchRow(), refetchDefaults()]);
      if (!cancelled) setLoading(false);
    })();
    const debounceMap = debounceRefs.current;
    return () => {
      cancelled = true;
      Object.values(debounceMap).forEach((id) => {
        if (id) clearTimeout(id);
      });
      if (pillTimerRef.current) clearTimeout(pillTimerRef.current);
    };
  }, [refetchRow, refetchDefaults]);

  const flashPill = useCallback((kind: 'success' | 'warning', label?: string) => {
    setPillKind(kind);
    setPillLabel(label);
    setPillVisible(true);
    if (pillTimerRef.current) clearTimeout(pillTimerRef.current);
    pillTimerRef.current = setTimeout(() => setPillVisible(false), PILL_VISIBLE_MS);
  }, []);

  const persist = useCallback(
    async (patch: Partial<ClientRow>) => {
      const { error } = await supabase.from('clients').update(patch).eq('id', clientId);
      if (error) {
        flashPill('warning', "Couldn't save — try again");
        return;
      }
      flashPill('success', 'Saved');
      // Pill provenance flips after the row changes — re-fetch the resolved
      // envelope so OverridePill sources update.
      await refetchDefaults();
    },
    [clientId, flashPill, refetchDefaults],
  );

  // Debounced setter: optimistic local update + delayed PostgREST write.
  const scheduleSave = useCallback(
    (key: string, patch: Partial<ClientRow>) => {
      setRow((prev) => (prev ? { ...prev, ...patch } : prev));
      const existing = debounceRefs.current[key];
      if (existing) clearTimeout(existing);
      debounceRefs.current[key] = setTimeout(() => {
        void persist(patch);
      }, SAVE_DEBOUNCE_MS);
    },
    [persist],
  );

  const tooltipReasoning = useMemo(() => {
    if (!tooltipFor || !reasoning) return null;
    return reasoning[tooltipFor];
  }, [tooltipFor, reasoning]);

  if (loading || !row || !sources || !resolved) {
    return (
      <section style={CARD}>
        <div style={SECTION_HEADER}>Money defaults</div>
        <div style={LOADING}>Loading…</div>
      </section>
    );
  }

  const pillFor = (field: keyof ClientDefaultsSources) => (
    <OverridePill
      source={sources[field]}
      clientName={clientName}
      onClick={() => setTooltipFor(field)}
    />
  );

  return (
    <section style={CARD}>
      <div style={HEADER_ROW}>
        <div>
          <div style={SECTION_HEADER}>Money defaults</div>
          <div style={SECTION_SUBTITLE}>Pre-fill on every invoice for this client</div>
        </div>
        <SaveConfirmationPill visible={pillVisible} kind={pillKind} label={pillLabel} />
      </div>

      <div style={DIVIDER} />

      <ClientDefaultsRow label="Payment terms" pill={pillFor('payment_terms_days')}>
        <div style={NUM_ROW}>
          <input
            type="number"
            value={row.default_payment_terms_days === null ? '' : String(row.default_payment_terms_days)}
            onChange={(e) => {
              const trimmed = e.target.value.trim();
              if (trimmed.length === 0) {
                scheduleSave('paymentTerms', { default_payment_terms_days: null });
                return;
              }
              const parsed = Number.parseInt(trimmed, 10);
              if (Number.isNaN(parsed) || parsed < 0 || parsed > 365) return;
              scheduleSave('paymentTerms', { default_payment_terms_days: parsed });
            }}
            placeholder={String(resolved.payment_terms_days)}
            min={0}
            max={365}
            style={NUM_INPUT}
          />
          <span style={AFFIX}>days</span>
        </div>
      </ClientDefaultsRow>

      <ClientDefaultsRow label="Deposit %" pill={pillFor('deposit_percentage')}>
        <div style={NUM_ROW}>
          <input
            type="number"
            value={row.default_deposit_percentage === null ? '' : String(row.default_deposit_percentage)}
            onChange={(e) => {
              const trimmed = e.target.value.trim();
              if (trimmed.length === 0) {
                scheduleSave('deposit', { default_deposit_percentage: null });
                return;
              }
              const parsed = Number.parseFloat(trimmed);
              if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return;
              scheduleSave('deposit', { default_deposit_percentage: parsed });
            }}
            placeholder={String(resolved.deposit_percentage)}
            min={0}
            max={100}
            step="0.01"
            style={NUM_INPUT}
          />
          <span style={AFFIX}>%</span>
        </div>
      </ClientDefaultsRow>

      <ClientDefaultsRow label="Late fees" pill={pillFor('late_fee_policy')}>
        <PolymorphicLateFeeEditor
          policy={row.default_late_fee_policy}
          amount={row.default_late_fee_amount}
          onChange={(next) =>
            scheduleSave('lateFee', {
              default_late_fee_policy: next.policy,
              default_late_fee_amount: next.amount,
            })
          }
        />
      </ClientDefaultsRow>

      <ClientDefaultsRow label="Tax treatment" pill={pillFor('tax_treatment')}>
        <TaxTreatmentSelector
          treatment={row.default_tax_treatment ?? 'inherit'}
          rateOverride={row.default_tax_rate_override}
          onChange={(next) =>
            scheduleSave('taxTreatment', {
              default_tax_treatment: next.treatment,
              default_tax_rate_override: next.rateOverride,
            })
          }
        />
      </ClientDefaultsRow>

      <ClientDefaultsRow label="Notes for invoices" pill={pillFor('notes_for_invoices')}>
        <textarea
          value={row.notes_for_invoices ?? ''}
          onChange={(e) => {
            const text = e.target.value;
            scheduleSave('notes', { notes_for_invoices: text.length === 0 ? null : text });
          }}
          placeholder="Auto-appended to invoice notes"
          style={TEXTAREA}
        />
      </ClientDefaultsRow>

      <OverridePillTooltip
        visible={tooltipFor !== null}
        reasoning={tooltipReasoning ?? ''}
        onDismiss={() => setTooltipFor(null)}
      />
    </section>
  );
}

const CARD: React.CSSProperties = {
  borderTop: `1px solid ${palette.g150}`,
  paddingTop: spacing.md,
  marginTop: spacing.sm,
  marginBottom: spacing.sm,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
};

const HEADER_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: spacing.sm,
};

const SECTION_HEADER: React.CSSProperties = {
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: palette.g600,
};

const SECTION_SUBTITLE: React.CSSProperties = {
  fontSize: typography.size.caption,
  color: palette.g500,
  marginTop: 2,
};

const DIVIDER: React.CSSProperties = {
  height: 1,
  background: palette.g100,
  marginBlock: spacing.sm,
};

const LOADING: React.CSSProperties = {
  fontSize: typography.size.caption,
  color: palette.g500,
  paddingBlock: spacing.md,
};

const NUM_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: radii.sm,
  paddingInline: spacing.md,
  alignSelf: 'flex-start',
  minWidth: 140,
};

const NUM_INPUT: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: typography.size.body,
  fontFamily: typography.fontFamily,
  color: palette.g800,
  paddingBlock: spacing.sm,
  width: 80,
};

const AFFIX: React.CSSProperties = {
  color: palette.g600,
  fontSize: typography.size.body,
};

const TEXTAREA: React.CSSProperties = {
  width: '100%',
  minHeight: 60,
  padding: spacing.sm,
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontSize: typography.size.body,
  color: palette.g800,
  fontFamily: typography.fontFamily,
  outline: 'none',
  resize: 'vertical',
  boxSizing: 'border-box',
};
